import uid from "./uid";
import {ISOCK_ACTION_EVENT_RECEIVED, IsockMessageGetFeaturePayload} from "../iframe-sock/iframeSocket";
import IframeSocket, {
    ISOCK_ACTION_CONNECT,
    ISOCK_ACTION_READY,
    IsockMessage,
    IsockMessageProxyCallPayload
} from '../iframe-sock/iframeSocket';
import {OpsPortalEventHandler} from "../types";
import {OpsPortalEvent} from "../types";
import {IOpsSdk} from "../types";
import {
    METHOD_ADD_EVENT_LISTENER,
    METHOD_CALL,
    METHOD_REFRESH_ACCESS_TOKEN,
    METHOD_REFRESH_GOOGLE_TOKEN,
    METHOD_REMOVE_EVENT_LISTENER,
    METHOD_GET_REFRESHED_ACCESS_TOKEN,
    syncSDKMethods,
    METHOD_GET_FEATURE
} from "./methods";

export default class Provider {

    sock: IframeSocket | null = null;
    window: Window;
    api: IOpsSdk;
    clientOrigin: string;

    eventProxy: OpsPortalEventHandler = (ev: OpsPortalEvent) => {
        this.sock.send({
            id: uid(),
            action: ISOCK_ACTION_EVENT_RECEIVED,
            payload: {
                event: ev
            },
            created: new Date()
        })
    };

    constructor(window: Window, api: IOpsSdk, bundleUrl: null | string = null) {
        this.window = window;
        this.api = api as IOpsSdk;
        this.clientOrigin = this.parseOriginFromPluginUrl(bundleUrl || '');

        this.window.top.onmessage = (e: MessageEvent) => {
            if (e.origin !== this.clientOrigin) {
                return;
            }

            try {
                const msg = JSON.parse(e.data) as IsockMessage;
                if (msg.action === ISOCK_ACTION_CONNECT && e.ports.length && e.ports[0]) {
                    const pluginPort = e.ports[0];
                    this.sock = new IframeSocket(pluginPort, 'PROVIDER');
                    this.setup(this.sock);
                    this.sock.send({...msg, ...{action: ISOCK_ACTION_READY, payload: ISOCK_ACTION_READY, status: 200}});
                }
            } catch (e) {
                console.log("Unable to establish client connection: " + e.message);
            }
        }
    }

    setup(socket: IframeSocket) {

        const syncApiHandler = (msg: IsockMessage) => {
            const fn = syncSDKMethods.get(msg.action);
            if (!fn) {
                throw new Error(`Cannot find method for '${msg.action}'`);
            }

            this.sock.send({...msg, ...{payload: fn(msg, this.api)}});
        };

        syncSDKMethods.forEach((fn, key) => {
            socket.on(key, syncApiHandler);
        });

        socket.on(METHOD_CALL, (msg) => {
            this.makeProxyCall(msg).then(
                result => this.sock.send({...msg, ...{payload: result}})
            ).catch(
                (e: any) => this.sock.send(this.buildErrorPayload(msg, e))
            );
        });

        socket.on(METHOD_ADD_EVENT_LISTENER, (msg) => {
            this.api.addEventListener(msg.payload.eventType, this.eventProxy);
            this.sock.send({...msg, payload: true});
        });
        socket.on(METHOD_REMOVE_EVENT_LISTENER, (msg) => {
            this.api.removeEventListener(msg.payload.eventType, this.eventProxy);
            this.sock.send({...msg, payload: true});
        });

        socket.on(METHOD_REFRESH_ACCESS_TOKEN, (msg) => {
            this.api.refreshAccessToken().then(() => {
                this.sock.send({...msg});
            }).catch(
              (e: any) => this.sock.send(this.buildErrorPayload(msg, e))
            );
        });

        socket.on(METHOD_GET_REFRESHED_ACCESS_TOKEN, (msg) => {
            this.api.getRefreshedAccessToken().then(token => {
                this.sock.send({...msg, payload: token});
            }).catch(
              (e: any) => this.sock.send(this.buildErrorPayload(msg, e))
            );
        });

        socket.on(METHOD_REFRESH_GOOGLE_TOKEN, (msg) => {
            this.api.refreshGoogleToken().finally(() => {
                this.sock.send({...msg});
            })
        });

        socket.on(METHOD_GET_FEATURE, (msg) => {
            const p = msg.payload as IsockMessageGetFeaturePayload;
            this.api.getFeature(p.featureId).then((result) => {
                this.sock.send({...msg, payload: result});
            }).catch(
              (e: any) => this.sock.send(this.buildErrorPayload(msg, e))
            );
        });
    }


    destroy() {
        this.sock?.destroy();
    }

    makeProxyCall(m: IsockMessage): Promise<any> {
        const p: IsockMessageProxyCallPayload = m.payload as IsockMessageProxyCallPayload;
        return this.api.call(
            p.url,
            p.data || {},
            p.method,
            p.headers || {},
            p.axiosOptions || {}
        );
    }

    parseOriginFromPluginUrl(bundleUrl: string): string {
        const parts = bundleUrl.split('/');

        if (parts.length >= 3) {
            return parts[0] + '//' + parts[2];
        }

        return '';
    }

    private buildErrorPayload(msg: IsockMessage, e: any): IsockMessage {
        return {...msg, ...{payload: e.message, status: e.response?.status ?? 500}}
    }
}
